<script setup>
    import { computed, inject, ref, watch } from "vue";
    import iPipelineLogo from "../../assets/images/iPipelineLogo.png";
    import underWriteMeLogo from "../../assets/images/underWriteMeLogo2.png";
    import IPipelineIntegration from "./IPipelineIntegration.vue";
    import UnderWriteMeIntegration from "./UnderWriteMeIntegration.vue";
    import { useUserIntegrations } from "../../queries/useUserIntegrations.js";
    import { useMutation } from "@tanstack/vue-query";
    import axios from 'axios';
    import { ElButton, ElRadio } from 'element-plus'

    const userService = inject('UserService');


    const { data: integrations } = useUserIntegrations();

    const currentIntegrations = computed(() =>
        integrations?.value?.map((i) => i.integrationType)
    );
    const selectedIntegration = ref("");
    const savedIntegration = ref("");

    const userDetails = computed(() => userService.getUserDetails());
    const defaultIntegration = ref(userDetails.value.defaultIntegration);

    const dialogVisible = ref(false);

    watch(() => userDetails.value, (newValue) => {
        defaultIntegration.value = newValue.defaultIntegration;
    });


    const { mutate: setIntegrationDefault } = useMutation({
        mutationFn: (value) => {
            defaultIntegration.value = value;
            axios.post("api/Pricing/SetUserDefaultIntegration/" + value)
        },
        onSuccess: () => {
            dialogVisible.value = false;
        }
    });

    const selectPricing = (integration) => {
        selectedIntegration.value = integration;
    }

    const goHome = () => { window.location.href = "/"; }

    function showDialog(value) {
        dialogVisible.value = true;
        savedIntegration.value = value;
    }
</script>

<template>
    <div class="mainDiv">
        <div class="integration-container bg-white">
            <h2 class="text-xl font-medium mb-4">
                We have integrated with third party underwriting and quote providers to enable you to compare quality alongside price.
            </h2>

            <p class="mb-3 font-same">
                If you have an account with any of the following solutions, we can pull through quotes.
            </p>

            <p class="mb-3 font-medium font-style">
                Add your account details to link or unlink the services for each supplier.
            </p>

            <p class="mb-6 font-same">
                The default service you select is the one we will use to generate pricing and quotes. You can change this at any time by selecting a different default option.
            </p>

            <div class="integration-section">
                <div class="integration-item-container">
                    <div class="integration-item left-section" style="background-color: #d5e7f7;">
                        <div @click="selectPricing('IPIPELINE')"
                             :class="{ 'logo': true, 'selected': selectedIntegration === 'IPIPELINE' }">
                            <img alt="iPipeline" :src="iPipelineLogo" />
                            <div v-if="currentIntegrations?.includes('IPIPELINE')" class="active">
                                Linked
                            </div>
                        </div>
                        <div>
                            <el-radio v-model="defaultIntegration" label="IPIPELINE"
                                      :disabled="!currentIntegrations?.includes('IPIPELINE')" @change="setIntegrationDefault('IPIPELINE')">
                                Make default
                            </el-radio>
                        </div>
                    </div>
                    <div class="right-section" style="background-color: #d5e7f7;">
                        <IPipelineIntegration @save-credentials="showDialog" />
                    </div>
                </div>

                <div class="integration-item-container">
                    <div class="integration-item left-section" style="background-color: #fae5d7;">
                        <div @click="selectPricing('UNDERWRITEME')"
                             :class="{ 'logo': true, 'underwriteme': true, 'selected': selectedIntegration === 'UNDERWRITEME' }">
                            <img alt="underWriteMe" :src="underWriteMeLogo" />
                            <div v-if="currentIntegrations?.includes('UNDERWRITEME')" class="active">
                                Linked
                            </div>
                        </div>
                        <div>
                            <el-radio v-model="defaultIntegration" label="UNDERWRITEME"
                                      :disabled="!currentIntegrations?.includes('UNDERWRITEME')" @change="setIntegrationDefault('UNDERWRITEME')">
                                Make default
                            </el-radio>
                        </div>
                    </div>
                    <div class="right-section" style="background-color: #fae5d7;">
                        <UnderWriteMeIntegration @save-credentials="showDialog" />
                    </div>
                </div>

                <div class="integration-item-container">
                    <div class="integration-item left-section" style="background-color: #f2f5f7;">
                        <div class="provider-logo no-price">
                            <p class="font-same">I do not want to include price in my comparisons</p>
                        </div>
                        <el-radio v-model="defaultIntegration" label="non-pricing" @change="setIntegrationDefault('non-pricing')">
                            Make default
                        </el-radio>
                    </div>
                    <div class="right-section empty" style="background-color: #f2f5f7;"></div>
                </div>
            </div>

            <br>

            <div style="display: flex; justify-content: center;">
                <el-button v-if="defaultIntegration" type="primary" @click="goHome()"> Continue to ProtectionGuruPro </el-button>
                <el-button v-else disabled type="info"> Select a default pricing option to continue</el-button>
            </div>
            <el-dialog v-model="dialogVisible" title="Thank you for linking your account." width="35%" :before-close="handleClose">
                <span>
                    <b>
                        Would you like to make {{ savedIntegration }} the default service that ProtectionGuruPro uses to obtain price
                        information?
                    </b> <br><br>
                    You can change the default at any time by clicking “My Account” and selecting “Price settings”.
                </span>
                <template #footer>
                    <span class="dialog-footer">
                        <div style="display: flex;">
                            <el-button @click="dialogVisible = false">No, thanks.</el-button>
                            <el-button type="primary" @click="setIntegrationDefault(savedIntegration)"> Yes, please. </el-button>
                        </div>
                    </span>
                </template>
            </el-dialog>
        </div>
    </div>
</template>
<style scoped>
    .font-style {
        font-weight: 700;
    }

    .font-same {
        font-weight: 600;
    }

    .integration-item-container {
        display: flex;
        margin-bottom: 1rem;
    }

    .integration-item {
        display: flex;
        flex-direction: row;
        width: 100%;
        padding: 1rem;
        box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.1);
        border-radius: 4px;
    }

    .left-section {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        width: 100px;
        padding: 3rem;
        flex-shrink: 0;
    }

    .right-section {
        flex-grow: 1;
        padding: 2rem;
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
    }

    .provider-logo {
        border: 1px solid #3c3c3c;
        border-radius: .5rem;
        padding: 0;
        width: 9rem;
        height: 6rem;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: #f5f5f5;
        box-sizing: border-box;
        position: relative;
        overflow: hidden;
        text-align: center;
        margin-bottom: 0.5rem;
    }

        .provider-logo img {
            width: 100%;
            object-fit: cover;
        }

    .logo {
        border: 1px solid #3c3c3c;
        border-radius: 0.5rem;
        padding: 0.5rem;
        width: 9rem;
        height: 6rem;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: #f5f5f5;
        box-sizing: border-box;
        cursor: pointer;
        position: relative;
        overflow: hidden;
        text-align: center;
    }

        .logo img {
            width: 100%;
            object-fit: cover;
        }

        .logo.underwriteme {
            padding: 0;
            background-color: #ffffff;
        }
            .logo.underwriteme img {
                object-fit: cover;
            }

        .logo.selected {
            border: 3px solid var(--el-color-primary);
        }

        .logo .active {
            position: absolute;
            inset: 0;
            top: auto;
            background-color: #49A267;
            color: var(--el-color-white);
        }

    .mainDiv {
        padding: 42px;
        background: #ffffff;
        border: 1px solid #e0e0e0;
        border-radius: 8px;
        max-width: 1044px;
        width: 100%;
        margin: 50px auto 0 auto;
        position: relative;
    }

    .dialog-footer {
        display: flex;
        justify-content: flex-end;
    }

</style>
